import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type InputHTMLAttributes, type ReactNode } from "react";

// A terminal prompt line: "> identify  carter@ranch.com█" or "> passcode  ********█".
// A real <input> sits underneath with transparent text, so typing, autofill,
// password managers, selection and screen readers all behave normally. A
// monospaced overlay draws the text (or one asterisk per character) and a
// blinking block cursor at the caret. Under reduced motion the block is steady.

type Native = Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type" | "className">;

export function TerminalInput({
  prompt,
  label,
  value,
  onChange,
  masked,
  type = "text",
  describedBy,
  invalid,
  idleCursor,
  ...rest
}: Native & {
  /** The visible prompt, e.g. "> identify". Hidden from screen readers. */
  prompt: ReactNode;
  /** The accessible name, e.g. "Email". */
  label: string;
  value: string;
  onChange: (v: string) => void;
  /** Show one asterisk per character. */
  masked?: boolean;
  type?: "text" | "email";
  describedBy?: string;
  invalid?: boolean;
  /** Show the blinking block at the end even without focus (the line waiting for input). */
  idleCursor?: boolean;
}) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLSpanElement>(null);
  const probeRef = useRef<HTMLSpanElement>(null);
  const [focused, setFocused] = useState(false);
  const [caret, setCaret] = useState(value.length);
  const [cap, setCap] = useState(Infinity);
  // Resets the blink so the cursor is solid right after each keystroke.
  const [blinkKey, setBlinkKey] = useState(0);

  const syncCaret = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    setCaret(el.selectionEnd ?? el.value.length);
    setBlinkKey((k) => k + 1);
  }, []);

  // How many characters fit, so a long value scrolls to keep the cursor visible.
  useLayoutEffect(() => {
    const box = boxRef.current;
    const probe = probeRef.current;
    if (!box || !probe) return;
    const measure = () => {
      const ch = probe.getBoundingClientRect().width / 10;
      if (ch > 0) setCap(Math.max(4, Math.floor(box.clientWidth / ch) - 1));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(box);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!focused) setCaret(value.length);
  }, [value, focused]);

  const shown = masked ? "*".repeat(value.length) : value;
  const at = Math.min(caret, shown.length);
  const shift = Number.isFinite(cap) ? Math.max(0, at + 1 - cap) : 0;
  const before = shown.slice(shift, at);
  const under = shown.charAt(at);
  const after = shown.slice(at + 1);

  return (
    <div className={`term-line${invalid ? " term-line--invalid" : ""}`}>
      <label htmlFor={id} className="term-line__prompt">
        <span aria-hidden="true">{prompt}</span>
        <span className="sr-only">{label}</span>
      </label>
      <span className="term-line__field" ref={boxRef}>
        <input
          {...rest}
          ref={inputRef}
          id={id}
          className="term-line__input"
          type={masked ? "password" : type}
          value={value}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          spellCheck={false}
          autoCapitalize="none"
          autoCorrect="off"
          onChange={(e) => {
            onChange(e.target.value);
            setCaret(e.target.selectionEnd ?? e.target.value.length);
            setBlinkKey((k) => k + 1);
          }}
          onSelect={syncCaret}
          onKeyUp={syncCaret}
          onFocus={(e) => {
            setFocused(true);
            syncCaret();
            rest.onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            rest.onBlur?.(e);
          }}
        />
        <span className="term-line__text" aria-hidden="true">
          {before}
          {focused ? (
            <span key={blinkKey} className="term-cursor">
              {under || " "}
            </span>
          ) : (
            under
          )}
          {after}
          {!focused && idleCursor ? <span className="term-cursor"> </span> : null}
        </span>
        <span className="term-line__probe" ref={probeRef} aria-hidden="true">
          0000000000
        </span>
      </span>
    </div>
  );
}
